// Activo Fijo → Catálogos → Ubicaciones (árbol físico).
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, Switch, Ubicacion, af, fieldCls } from './shared';

const vacio = { codigo: '', nombre: '', parent_id: '', es_activo: true };

export function UbicacionesAF() {
  const { t } = useTranslation('activofijo');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Ubicacion[]>({
    queryKey: ['af-ubicaciones'],
    queryFn: () => af.list<Ubicacion>('ubicaciones', '?select=*&deleted_at=is.null&order=codigo,nombre'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (p: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...p }));
  const nombrePorId = useMemo(() => Object.fromEntries(rows.map((u) => [u.id, u.nombre])), [rows]);

  const abrir = (u?: Ubicacion) => {
    if (u) setForm({ codigo: u.codigo ?? '', nombre: u.nombre, parent_id: u.parent_id ?? '', es_activo: u.es_activo });
    else setForm(vacio);
    setEditId(u?.id ?? null);
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { codigo: form.codigo || null, nombre: form.nombre.trim(), parent_id: form.parent_id || null, es_activo: form.es_activo };
      return editId ? af.update('ubicaciones', editId, body) : af.create('ubicaciones', body);
    },
    onSuccess: () => { toast.success(t('ubicaciones.toast_guardada')); setAbierto(false); void qc.invalidateQueries({ queryKey: ['af-ubicaciones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => af.remove('ubicaciones', id),
    onSuccess: () => { toast.success(t('ubicaciones.toast_eliminada')); void qc.invalidateQueries({ queryKey: ['af-ubicaciones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><MapPin className="w-5 h-5" /> {t('ubicaciones.titulo')}</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> {t('comun.nueva')}</Button>
      </div>
      <p className="text-sm text-muted-foreground">{t('ubicaciones.intro')}</p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('ubicaciones.n_ubicaciones', { n: rows.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('comun.codigo')}</th><th className="py-2 pr-3">{t('comun.nombre')}</th><th className="py-2 pr-3">{t('ubicaciones.col_padre')}</th><th className="py-2 pr-3">{t('comun.estado')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('ubicaciones.sin_ubicaciones')}</td></tr>}
              {rows.map((u) => (
                <tr key={u.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{u.codigo ?? '—'}</td>
                  <td className="py-2 pr-3 font-medium">{u.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{u.parent_id ? nombrePorId[u.parent_id] ?? '—' : '—'}</td>
                  <td className="py-2 pr-3">{u.es_activo ? t('comun.activa') : t('comun.inactiva')}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('comun.editar')} onClick={() => abrir(u)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title={t('comun.eliminar')} onClick={async () => { if (await dlg.confirmar(t('categorias.confirmar_eliminar', { nombre: u.nombre }), { peligro: true })) eliminar.mutate(u.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? t('ubicaciones.editar') : t('ubicaciones.nueva')} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Campo label={t('comun.codigo')}><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value.toUpperCase() })} /></Campo>
              <Campo label={t('comun.nombre_req')} className="col-span-2"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            </div>
            <Campo label={t('ubicaciones.col_padre')}>
              <select className={fieldCls} value={form.parent_id} onChange={(e) => set({ parent_id: e.target.value })}>
                <option value="">{t('ubicaciones.sin_padre')}</option>
                {rows.filter((u) => u.id !== editId).map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
              </select>
            </Campo>
            <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label={t('comun.activa')} />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>{t('comun.cancelar')}</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>{t('comun.guardar')}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
